import { FC, useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
} from "@material-ui/core";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import FormikForm, {
  FormikStep,
} from "src/components/ui-components/FormikForm";
import * as Yup from "yup";
import InputField from "src/components/ui-components/InputField";
import ConfigUserGroupType from "../userGroups/Types/ConfigUserGroup";
import SelectField from "src/components/ui-components/SelectField";
import {
  getConfigUserInvitationByKey,
  insertConfigUserInvitation,
  updateConfigUserInvitation,
} from "./ConnectionService";

type ModalProps = {
  open: boolean;
  handleClose: any;
  invitationKey?: string | null;
  userGroupsList: ConfigUserGroupType[];
};

const defaultForm = {
  UserInvitationKey: null,
  Email: "",
  UserRoleKey: "",
  AppKey: "",
  InviteCode: "",
  IsInviteSent: "",
  ExpiresOn: "",
  InvalidatedOn: "",
};

const InviteUserModal: FC<ModalProps> = ({
  open,
  handleClose,
  invitationKey,
  userGroupsList,
}) => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef(null);

  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, isEdit: invitationKey && true },
      });
    }
  };

  const fetchUserInvitation = async () => {
    setIsFetching(true);
    const res = await getConfigUserInvitationByKey(invitationKey);
    resetFormData(res);
    setIsFetching(false);
  };

  const handleUserGroupChange = (newValue) => {
    formRef.current.setFieldValue('UserRoleKey', newValue.ConfigUserGroupKey);
  };

  useEffect(() => {
    if (invitationKey) {
      fetchUserInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationKey]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Extend or Edit Invite"
        aria-describedby="Form to Extend or Edit User Invite"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "70%",
          }}
        >
          <Typography
            sx={{ marginBottom: "2%", fontWeight: "bold" }}
            variant="h5"
          >
            {`${invitationKey ? "Edit" : "Create"} Invite`}
          </Typography>
          {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
          <Box sx={{ display: isFetching ? "none" : "" }}>
            <FormikForm
              innerRef={formRef}
              initialValues={defaultForm}
              onSubmit={async (values): Promise<void> => {
                setIsLoading(true);
                try {
                  if (invitationKey) {
                    const res = await updateConfigUserInvitation(values);
                    if (res) {
                      handleClose();
                    }
                  } else {
                    const payload = {
                      userEmail: values.Email,
                      configUserGroupKey: values.UserRoleKey,
                    };
                    const res = await insertConfigUserInvitation(payload);
                    if (res) {
                      handleClose();
                    }
                  }
                } catch (error) {
                  console.error("Error submitting form:", error);
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              <FormikStep
                validationSchema={Yup.object({
                  Email: Yup.string()
                    .nullable()
                    .required("Email is required")
                    .email(),
                  UserRoleKey: Yup.string()
                    .nullable()
                    .required("User Group name is required")
                    .max(
                      100,
                      "User Group name cannot be more than 100 characters"
                    )
                    .matches(
                      /^(?!\s).+(?<!\s)$/gm,
                      "Please enter valid User Group name"
                    ),
                })}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <InputField
                      label="User Email *"
                      name="Email"
                      disabled={isLoading || Boolean(invitationKey)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <SelectField
                      label="User Group"
                      data={userGroupsList}
                      dataTextField='ConfigUserGroup'
                      dataValueField='ConfigUserGroupKey'
                      name='UserRoleKey'
                      disableClearable
                      onChange={handleUserGroupChange}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} justifyContent="end" marginTop="2%">
                  <Button
                    sx={{
                      minWidth: 100,
                      marginRight: "2%",
                      color: "rgb(107, 119, 140)",
                    }}
                    disabled={isLoading}
                    variant="outlined"
                    type="button"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    sx={{
                      background:
                        "linear-gradient(to bottom, #072f40, #072f40)",
                      minWidth: 100,
                    }}
                    disabled={isLoading}
                    variant="contained"
                  >
                    Save
                    {isLoading && (
                      <CircularProgress
                        color="inherit"
                        thickness={5}
                        size={20}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </Grid>
              </FormikStep>
            </FormikForm>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default InviteUserModal;
